/** One row of `/smart-dispatch/apply-preplan`'s results. */
export interface ApplyResultRow {
  pronumber: number;
  legnum: number;
  driver: string;
  success: boolean;
  error?: string;
  /** Held back by a hard stop before anything wrote — today, an open rejection ticket. */
  blocked?: boolean;
}

export interface AppliedMove { pronumber: number; legnum: number; driver: string }
export interface HeldMove extends AppliedMove { error: string }

/** The apply's outcome as the results dialog reads it (planner feedback, 2026-10-29):
 *  - success: applied.
 *  - blocked: nothing on that PRO was touched — the leg kept the driver it had — and a
 *    retry is refused again until the stop is cleared, so it is not offered one.
 *  - failed: tried and refused by the save itself, which a retry can get past.
 * One blocked PRO used to fail the whole board with a bare 422; now it is one row. */
export function splitApplyResults(rows: ApplyResultRow[]) {
  const move = (r: ApplyResultRow): AppliedMove => ({ pronumber: r.pronumber, legnum: r.legnum, driver: r.driver });
  const held = (r: ApplyResultRow): HeldMove => ({ ...move(r), error: r.error || 'Unknown error' });
  return {
    success: rows.filter(r => r.success).map(move),
    blocked: rows.filter(r => !r.success && r.blocked).map(held),
    failed: rows.filter(r => !r.success && !r.blocked).map(held),
  };
}

/** Why the whole apply failed, in the server's words rather than axios's
 * "Request failed with status code 422". */
export function applyError(err: unknown): string {
  const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) {
    const messages = detail.map(d => (d as { msg?: string })?.msg).filter(Boolean);
    if (messages.length) return messages.join('; ');
  }
  return e?.message || 'Apply failed';
}
